import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import TrendingTemplate from 'components/trending/TrendingTemplate';
import TrendingSection from 'components/trending/TrendingSection';
import TrendingBannerCards from 'containers/list/TrendingBannerCards';
import SectionThumbnail from 'components/trending/SectionThumbnail';
import BadgeList from 'components/trending/BadgeList';
import ProductCardList from 'components/common/ProductCardList';
import type { State } from 'store';
import { TrendingActions } from 'store/actionCreators';

type Props = {
  sections: any,
  badges: any,
  selectedBadge: any,
  products: any[],
  hasMore: boolean,
  loading: boolean,
  shouldCancel: boolean,
  history: any,
};

type TrendingState = {
  showLeftBtn: boolean,
  showRightBtn: boolean,
};

class Trending extends Component<Props, TrendingState> {
  sectionScrollRef = React.createRef();

  state = {
    showLeftBtn: false,
    showRightBtn: false,
  };

  initialize = async () => {
    const { shouldCancel, sections, badges } = this.props;
    try {
      if (!shouldCancel) {
        if (!sections || sections.length === 0) {
          await TrendingActions.getSectionsWithBadges();
        }
        if (!badges || badges.length === 0) {
          await TrendingActions.getAllBadges();
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  componentDidMount() {
    this.initialize();
  }

  componentDidUpdate(prevProps: Props) {
    const { badges, selectedBadge, sections } = this.props;

    if (badges && badges.length > 0 && !selectedBadge && prevProps.badges !== badges) {
      const firstBadge = badges[0];
      this.handleBadgeClick(firstBadge);
    }

    if (sections && sections !== prevProps.sections) {
      setTimeout(() => this.updateScrollButtons(), 100);
    }
  }

  componentWillUnmount() {
    const container = this.sectionScrollRef.current;
    if (container) {
      container.removeEventListener('scroll', this.updateScrollButtons);
    }
  }

  updateScrollButtons = () => {
    const container = this.sectionScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const showLeftBtn = scrollLeft > 10;
    const showRightBtn = scrollLeft < scrollWidth - clientWidth - 10;

    this.setState({ showLeftBtn, showRightBtn });
  };

  handleSectionClick = (section: any) => {
    const { history } = this.props;
    if (section.url_slug) {
      if (section.link_type === 'category') {
        const facets = JSON.stringify({ categories: section.url_slug });
        history.push(`/search?q=&menu=products&facets=${encodeURIComponent(facets)}`);
      } else if (section.link_type === 'brand') {
        const facets = JSON.stringify({ brand: section.url_slug });
        history.push(`/search?q=&menu=products&facets=${encodeURIComponent(facets)}`);
      } else {
        history.push(`/sections/${section.url_slug}`);
      }
    }
  };

  handleBadgeClick = (badge: any) => {
    TrendingActions.setSelectedBadge(badge);
    if (badge.products && badge.products.length > 0) {
      const products = badge.products.map(product => ({
        ...product,
        badges: [{ id: badge.id, name: badge.name, color: badge.color }],
      }));
      TrendingActions.setProducts(products);
    } else {
      TrendingActions.clearProducts();
      TrendingActions.getBadgeProducts(badge.id);
    }
  };

  scrollSections = (direction: 'left' | 'right') => {
    const container = this.sectionScrollRef.current;
    if (!container) return;

    const scrollAmount = 400;
    const targetScroll =
      direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  render() {
    const { sections, badges, selectedBadge, products } = this.props;

    return (
      <TrendingTemplate>
        <Helmet>
          <title>연구원들은 다 아는 쇼핑몰 | 캐시바이</title>
          <link rel="canonical" href="https://cacheby.com" />
          <meta
            name="description"
            content="연구제품 전문가들에게 빠른 상담을 통해 궁금한 부분을 해결하세요. 견적서와 발주서가 없이도 제품 구매가 가능해요. 원하는 제품을 쉽게 구매하세요."
          />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="og:title" content="연구원들은 다 아는 쇼핑몰 | 캐시바이" />
          <meta name="og:type" content="website" />
          <meta name="og:url" content="https://cacheby.com" />
          <meta name="og:image" content="https://images.cacheby.com/cacheby.png" />
          <meta
            name="og:description"
            content="연구제품 전문가들에게 빠른 상담을 통해 궁금한 부분을 해결하세요. 견적서와 발주서가 없이도 제품 구매가 가능해요. 원하는 제품을 쉽게 구매하세요."
          />
        </Helmet>
        <TrendingSection color="white">
          <TrendingBannerCards />
        </TrendingSection>

        {sections && sections.length > 0 && (
          <TrendingSection color="white" className="hide-title-mobile">
            <div style={{ position: 'relative' }}>
              {this.state.showLeftBtn && (
                <button
                  className="section-scroll-btn section-scroll-btn-left"
                  onClick={() => this.scrollSections('left')}
                  aria-label="이전 섹션"
                >
                  <IoIosArrowBack />
                </button>
              )}
              {this.state.showRightBtn && (
                <button
                  className="section-scroll-btn section-scroll-btn-right"
                  onClick={() => this.scrollSections('right')}
                  aria-label="다음 섹션"
                >
                  <IoIosArrowForward />
                </button>
              )}
              <div
                ref={this.sectionScrollRef}
                className="section-scroll-container"
                onScroll={this.updateScrollButtons}
                style={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: '4rem',
                  padding: '1.5rem 0.5rem 0.5rem 0.5rem',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {sections.map(section => (
                  <SectionThumbnail
                    key={section.id}
                    thumbnail={
                      section.thumbnail || 'https://images.cacheby.com/default_thumbnail.png'
                    }
                    title={section.title}
                    description={section.description}
                    onClick={() => this.handleSectionClick(section)}
                  />
                ))}
              </div>
            </div>
            <style>{`
              .section-scroll-container::-webkit-scrollbar {
                display: none;
              }
              .section-scroll-btn {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                width: 48px;
                height: 48px;
                border-radius: 50%;
                background: white;
                border: 1px solid #e0e0e0;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                z-index: 10;
                transition: all 0.2s ease;
                font-size: 24px;
                color: #333;
              }
              .section-scroll-btn:hover {
                background: #f8f9fa;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                transform: translateY(-50%) scale(1.05);
              }
              .section-scroll-btn:active {
                transform: translateY(-50%) scale(0.95);
              }
              .section-scroll-btn-left {
                left: -24px;
              }
              .section-scroll-btn-right {
                right: -24px;
              }
              @media (max-width: 768px) {
                .section-scroll-container {
                  gap: 2rem !important;
                  padding: 1rem 0.5rem 0.5rem 1.0rem !important;
                }
                .section-scroll-btn {
                  display: none;
                }
              }
            `}</style>
          </TrendingSection>
        )}

        {badges && badges.length > 0 && (
          <div id="badge-list-section">
            <TrendingSection color="white">
              <BadgeList
                badges={badges}
                selectedBadgeId={selectedBadge ? selectedBadge.id : null}
                onBadgeClick={this.handleBadgeClick}
              />
            </TrendingSection>
          </div>
        )}

        {selectedBadge && products && products.length > 0 && (
          <TrendingSection color="white">
            <ProductCardList products={products} />
          </TrendingSection>
        )}
      </TrendingTemplate>
    );
  }
}

export default connect(
  ({ trending, common }: State) => ({
    sections: trending.sections,
    badges: trending.badges,
    selectedBadge: trending.selectedBadge,
    products: trending.products,
    hasMore: trending.hasMore,
    loading: trending.loading,
    shouldCancel: common.ssr && !common.router.altered,
  }),
  () => ({}),
)(Trending);



// WEBPACK FOOTER //
// ./src/pages/Trending.js